import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  MutableRefObject,
} from 'react';
import { CategoricalChartState } from 'recharts/types/chart/types';

export type ZoomRange = { min: number; max: number };
type ChartMode = 'zoom' | 'exclude';

interface UseChartOptions<T> {
  data: T[];
  getXValue: (item: T) => number;
  chartRef: MutableRefObject<HTMLDivElement | null>;
  mode: ChartMode;
  onExcludeRange?: (start: number, end: number) => void;
  zoomFactor?: number;
  maxVisiblePoints?: number;
  trainingPeriodEnd?: number;
}

const MAX_VISIBLE_POINTS = 5;
const ZOOM_FACTOR = 0.2;

const useChartSelection = <T>({
  data,
  getXValue,
  chartRef,
  mode,
  onExcludeRange,
  zoomFactor = ZOOM_FACTOR,
  maxVisiblePoints = MAX_VISIBLE_POINTS,
  trainingPeriodEnd,
}: UseChartOptions<T>) => {
  // Zoom state
  const [zoom, setZoom] = useState(1);
  const [zoomRange, setZoomRange] = useState<ZoomRange | null>(null);

  // Exclude state
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [excludeRange, setExcludeRange] = useState<{
    start: number;
    end: number;
  } | null>(null);

  // Selection state
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<number | null>(null);
  const [selectionStartX, setSelectionStartX] = useState<number | null>(null);
  const [selectionCurrentX, setSelectionCurrentX] = useState<number | null>(
    null,
  );
  const [chartBounds, setChartBounds] = useState<DOMRect | null>(null);

  const initialMin = useMemo(
    () => data.reduce((min, item) => Math.min(min, getXValue(item)), Infinity),
    [data, getXValue],
  );
  const initialMax = useMemo(
    () => data.reduce((max, item) => Math.max(max, getXValue(item)), -Infinity),
    [data, getXValue],
  );

  const zoomedData = useMemo(() => {
    if (!zoomRange) return data;

    return data.filter((item) => {
      const x = getXValue(item);
      return x >= zoomRange.min && x <= zoomRange.max;
    });
  }, [data, zoomRange, getXValue]);

  const firstDetectionTimestamp = useMemo(() => {
    const first = zoomedData.find((item: any) => item?.isInDetectionPeriod);
    return first ? getXValue(first) : undefined;
  }, [zoomedData, getXValue]);

  const isMaxZoom = useMemo(() => {
    const visiblePoints = zoomRange
      ? data.filter((item) => {
          const x = getXValue(item);
          return x >= zoomRange.min && x <= zoomRange.max;
        })
      : data;

    return visiblePoints.length <= maxVisiblePoints;
  }, [zoomRange, data, getXValue, maxVisiblePoints]);

  // Zoom functions
  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setZoomRange(null);
  }, []);

  const handleZoomIn = useCallback(() => {
    if (isMaxZoom) return;

    const currentMin = zoomRange?.min ?? initialMin;
    const currentMax = zoomRange?.max ?? initialMax;

    const visibleData = data.filter((item) => {
      const x = getXValue(item);
      return x >= currentMin && x <= currentMax;
    });

    if (visibleData.length <= maxVisiblePoints) return;

    const newTargetCount = Math.max(
      maxVisiblePoints,
      Math.floor(visibleData.length * (1 - zoomFactor)),
    );

    const newVisibleData = visibleData.slice(-newTargetCount);

    const newMin = getXValue(newVisibleData[0]);
    const newMax = getXValue(newVisibleData[newVisibleData.length - 1]);

    setZoomRange({ min: newMin, max: newMax });
    setZoom((s) => s + 1);
  }, [
    isMaxZoom,
    zoomRange,
    initialMin,
    initialMax,
    data,
    getXValue,
    maxVisiblePoints,
    zoomFactor,
  ]);

  const handleZoomOut = useCallback(() => {
    const currentMin = zoomRange?.min ?? initialMin;
    const currentMax = zoomRange?.max ?? initialMax;

    if (currentMin <= initialMin && currentMax >= initialMax) {
      handleResetZoom();
      return;
    }

    const currentVisible = data.filter((item) => {
      const x = getXValue(item);
      return x >= currentMin && x <= currentMax;
    });

    const currentCount = currentVisible.length;
    const newTargetCount = Math.min(
      data.length,
      Math.ceil(currentCount * (1 + zoomFactor)),
    );

    const rightAlignedData = data.filter(
      (item) => getXValue(item) <= currentMax,
    );
    const newVisible = rightAlignedData.slice(-newTargetCount);

    if (newVisible.length === 0) return;

    const newMin = getXValue(newVisible[0]);
    const newMax = getXValue(newVisible[newVisible.length - 1]);

    if (newMin <= initialMin && newMax >= initialMax) {
      handleResetZoom();
    } else {
      setZoomRange({ min: newMin, max: newMax });
      setZoom((s) => Math.max(1, s - 1));
    }
  }, [
    zoomRange,
    initialMin,
    initialMax,
    data,
    getXValue,
    zoomFactor,
    handleResetZoom,
  ]);

  const handleZoomSelection = useCallback(
    (start: number, end: number) => {
      const selMin = Math.min(start, end);
      const selMax = Math.max(start, end);
      const range = selMax - selMin;
      const padding = range * 0.1;
      const paddedMin = Math.max(
        data[0] ? getXValue(data[0]) : 0,
        selMin - padding,
      );
      const paddedMax = Math.min(
        data[data.length - 1] ? getXValue(data[data.length - 1]) : 0,
        selMax + padding,
      );
      setZoomRange({ min: paddedMin, max: paddedMax });
    },
    [data, getXValue],
  );

  // Exclude functions
  const handleExcludeConfirm = useCallback(() => {
    if (excludeRange && onExcludeRange) {
      onExcludeRange(excludeRange.start, excludeRange.end);
    }
    setExcludeDialogOpen(false);
    setExcludeRange(null);
  }, [excludeRange, onExcludeRange]);

  const handleExcludeCancel = useCallback(() => {
    setExcludeDialogOpen(false);
    setExcludeRange(null);
  }, []);

  const openExcludeDialog = useCallback(
    (start: number, end: number) => {
      const fixedEnd = trainingPeriodEnd
        ? end > trainingPeriodEnd
          ? trainingPeriodEnd
          : end
        : end;

      setExcludeRange({ start, end: fixedEnd });
      setExcludeDialogOpen(true);
    },
    [trainingPeriodEnd],
  );

  const handleExcludeSelection = useCallback(
    (start: number, end: number) => {
      let clampedStart = start;
      let clampedEnd = end;
      if (firstDetectionTimestamp) {
        // Find the last training datapoint (the one before firstDetectionTimestamp)
        const firstDetectionIndex = zoomedData.findIndex(
          (item) => getXValue(item) === firstDetectionTimestamp,
        );
        const lastTrainingDatapoint =
          firstDetectionIndex > 0 ? zoomedData[firstDetectionIndex - 1] : null;
        const lastTrainingTimestamp = lastTrainingDatapoint
          ? getXValue(lastTrainingDatapoint)
          : firstDetectionTimestamp;

        clampedStart = Math.min(start, lastTrainingTimestamp);
        clampedEnd = Math.min(end, lastTrainingTimestamp);

        // If the time selected was after the last training datapoint, use the last training datapoint
        if (clampedEnd > lastTrainingTimestamp) {
          clampedEnd = lastTrainingTimestamp;
        }
      }
      const startTime = Math.min(clampedStart, clampedEnd);
      const endTime = Math.max(clampedStart, clampedEnd);
      openExcludeDialog(startTime, endTime);
    },
    [firstDetectionTimestamp, openExcludeDialog, zoomedData, getXValue],
  );

  // Selection functions
  const resetSelection = useCallback(() => {
    setSelectionStart(null);
    setSelectionEnd(null);
    setSelectionStartX(null);
    setSelectionCurrentX(null);
  }, []);

  const handleSelectionComplete = useCallback(
    (start: number, end: number) => {
      if (mode === 'zoom') {
        handleZoomSelection(start, end);
      } else if (mode === 'exclude') {
        handleExcludeSelection(start, end);
      }
    },
    [mode, handleZoomSelection, handleExcludeSelection],
  );

  const handleMouseDown = useCallback(
    (e: CategoricalChartState) => {
      if (chartRef?.current) {
        setChartBounds(chartRef.current.getBoundingClientRect());
      }

      const numberLabel = Number(e.activeLabel);

      if (
        mode === 'exclude' &&
        firstDetectionTimestamp &&
        zoomRange &&
        e.activeLabel !== undefined &&
        Number(e.activeLabel) >= firstDetectionTimestamp
      ) {
        return;
      }

      setSelectionStart(numberLabel ?? null);
      setSelectionStartX(e?.chartX ?? null);
    },
    [mode, firstDetectionTimestamp, zoomRange, chartRef],
  );

  const handleMouseMove = useCallback(
    (e: any) => {
      if (selectionStartX != null && typeof e?.chartX !== 'undefined') {
        setSelectionCurrentX(e.chartX);
      }

      if (selectionStart != null) {
        let newX = e?.activeLabel;

        if (
          mode === 'exclude' &&
          firstDetectionTimestamp &&
          zoomRange &&
          newX !== undefined &&
          newX >= firstDetectionTimestamp
        ) {
          newX = firstDetectionTimestamp;
        }

        if (typeof e?.activeLabel === 'undefined' && chartBounds) {
          const clientX = e?.nativeEvent?.clientX;

          if (typeof clientX === 'number') {
            const percentX = (clientX - chartBounds.left) / chartBounds.width;
            const newValue = initialMin + percentX * (initialMax - initialMin);
            newX = Math.max(initialMin, Math.min(initialMax, newValue));
          }
        }

        if (newX != null) {
          setSelectionEnd(newX);
        }
      }
    },
    [
      selectionStartX,
      selectionStart,
      chartBounds,
      initialMin,
      initialMax,
      mode,
      firstDetectionTimestamp,
      zoomRange,
    ],
  );

  const handleMouseUp = useCallback(() => {
    const allowSingleSampleSelect = mode !== 'zoom';
    const isSelectionValid =
      selectionStart === selectionEnd ? allowSingleSampleSelect : true;

    if (selectionStart != null && selectionEnd != null && isSelectionValid) {
      const start = Math.min(selectionStart, selectionEnd);
      const end = Math.max(selectionStart, selectionEnd);
      handleSelectionComplete(start, end);
    }

    resetSelection();
  }, [
    selectionStart,
    selectionEnd,
    resetSelection,
    mode,
    handleSelectionComplete,
  ]);

  // Global event listeners for handling mouse events outside the chart
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        resetSelection();
      }
    };

    // Helper function to get chart boundaries from chartRef
    const getChartBoundaries = () => {
      if (!chartRef?.current) return null;

      const g = chartRef.current.querySelector('svg g');
      if (!g) return null;

      const bounds = chartRef.current.getBoundingClientRect();
      const innerChartBounds = g.getBoundingClientRect();

      return { bounds, innerChartBounds };
    };

    // Helper function to clamp mouse position within chart bounds
    const clampMousePosition = (
      clientX: number,
      bounds: DOMRect,
      innerChartBounds: DOMRect,
    ) => {
      const clampedClientX = Math.max(
        innerChartBounds.left,
        Math.min(innerChartBounds.right, clientX),
      );

      const relativeX = clampedClientX - bounds.left;
      return { clampedClientX, relativeX };
    };

    // Helper function to calculate estimated timestamp from relative X position
    const calculateEstimatedTimestamp = (
      relativeX: number,
      bounds: DOMRect,
      timeMin: number,
      timeMax: number,
    ) => {
      const timeRange = timeMax - timeMin;
      const fraction = Math.max(0, Math.min(1, relativeX / bounds.width));
      let estimatedTimestamp = timeMin + timeRange * fraction;

      if (data.length > 0) {
        const relevantData = data.filter((item) => {
          const x = getXValue(item);
          return x >= timeMin && x <= timeMax;
        });

        if (relevantData.length > 0) {
          const closestItem = relevantData.reduce((closest, current) => {
            const closestDiff = Math.abs(
              getXValue(closest) - estimatedTimestamp,
            );
            const currentDiff = Math.abs(
              getXValue(current) - estimatedTimestamp,
            );
            return currentDiff < closestDiff ? current : closest;
          });

          estimatedTimestamp = getXValue(closestItem);
        }
      }

      if (mode === 'exclude' && firstDetectionTimestamp) {
        estimatedTimestamp = Math.min(
          estimatedTimestamp,
          firstDetectionTimestamp,
        );
      }

      return estimatedTimestamp;
    };

    const handleMouseMoveOutside = (e: MouseEvent) => {
      if (selectionStartX == null || !chartRef?.current) return;

      const boundaries = getChartBoundaries();
      if (!boundaries) return;

      const { bounds, innerChartBounds } = boundaries;

      const isInsideChart =
        e.clientX >= innerChartBounds.left &&
        e.clientX <= innerChartBounds.right &&
        e.clientY >= innerChartBounds.top &&
        e.clientY <= innerChartBounds.bottom;

      if (isInsideChart) return;

      const { relativeX } = clampMousePosition(
        e.clientX,
        bounds,
        innerChartBounds,
      );
      setSelectionCurrentX(relativeX);

      if (e.clientX < innerChartBounds.left) {
        setSelectionEnd(initialMin);
        return;
      }

      if (e.clientX > innerChartBounds.right) {
        setSelectionEnd(initialMax);
        return;
      }

      const timeMin = zoomRange?.min ?? initialMin;
      const timeMax = zoomRange?.max ?? initialMax;

      const estimatedTimestamp = calculateEstimatedTimestamp(
        relativeX,
        bounds,
        timeMin,
        timeMax,
      );

      setSelectionEnd(estimatedTimestamp);
    };

    const handleMouseUpOutside = () => {
      handleMouseUp();
    };

    if (selectionStartX !== null) {
      window.addEventListener('keydown', handleKeyDown, true);
      window.addEventListener('mouseup', handleMouseUpOutside);
      window.addEventListener('mousemove', handleMouseMoveOutside);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('mouseup', handleMouseUpOutside);
      window.removeEventListener('mousemove', handleMouseMoveOutside);
    };
  }, [
    selectionStartX,
    selectionEnd,
    mode,
    firstDetectionTimestamp,
    zoomRange,
    data,
    getXValue,
    chartRef,
    initialMin,
    initialMax,
    handleMouseUp,
    resetSelection,
  ]);

  // Calculate clamped selection position for exclude mode visual display
  const clampedSelectionCurrentX =
    mode === 'exclude' &&
    firstDetectionTimestamp &&
    zoomRange &&
    selectionStartX !== null &&
    selectionCurrentX !== null
      ? (() => {
          const chartRect = chartRef.current?.getBoundingClientRect();
          if (chartRect) {
            const chartMargin = { left: -14, right: 4 };
            const chartWidth =
              chartRect.width - chartMargin.left - chartMargin.right;
            const domainSpan = zoomRange.max - zoomRange.min;
            const detectionBoundaryRatio =
              (firstDetectionTimestamp - zoomRange.min) / domainSpan;
            const detectionBoundaryX = detectionBoundaryRatio * chartWidth;
            return Math.min(selectionCurrentX, detectionBoundaryX);
          }
          return selectionCurrentX;
        })()
      : selectionCurrentX;

  // Calculate timestamp preview text
  const selectionTimeText =
    selectionStartX !== null &&
    selectionCurrentX !== null &&
    selectionStart !== null &&
    selectionEnd !== null
      ? (() => {
          let timeStart = Math.min(selectionStart, selectionEnd);
          let timeEnd = Math.max(selectionStart, selectionEnd);

          if (mode === 'exclude' && firstDetectionTimestamp) {
            timeStart = Math.min(timeStart, firstDetectionTimestamp);
            timeEnd = Math.min(timeEnd, firstDetectionTimestamp);
          }

          return { timeStart, timeEnd };
        })()
      : null;

  return {
    // Zoom functionality
    zoomedData,
    zoomRange,
    isMaxZoom,
    setZoomRange,
    setZoom,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    zoom,

    // Exclude functionality
    excludeDialogOpen,
    excludeRange,
    handleExcludeConfirm,
    handleExcludeCancel,
    openExcludeDialog,

    // Selection functionality
    selectionStart,
    selectionEnd,
    selectionStartX,
    selectionCurrentX,
    clampedSelectionCurrentX,
    selectionTimeText,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    resetSelection,

    // Shared
    firstDetectionTimestamp,
  };
};

export default useChartSelection;
